import React from 'react';
import * as d3 from 'd3';

import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import BallotIcon from '@mui/icons-material/Ballot';
import BlurLinearIcon from '@mui/icons-material/BlurLinear';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import DescriptionIcon from '@mui/icons-material/Description';
import AssistantIcon from '@mui/icons-material/Assistant';
import LabelIcon from '@mui/icons-material/Label';
import GetAppIcon from '@mui/icons-material/GetApp';
import { LocalOffer } from '@mui/icons-material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { GENDER } from 'src/components/questionnaire/Constants';
import LaunchIcon from '@mui/icons-material/Launch';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import NoteIcon from '@mui/icons-material/Note';
import DataObjectIcon from '@mui/icons-material/DataObject';
import InsightsIcon from '@mui/icons-material/Insights';
import LinkIcon from '@mui/icons-material/Link';

// SIDEBAR_VALUE is defined in the dependency-free leaf module ./sidebarValues so
// the Node vocabulary generator can import it without this file's d3/MUI deps.
// Imported here for local use (SIDEBAR_ITEMS below) and re-exported so existing
// importers of `SIDEBAR_VALUE` from './util' are unaffected.
import { SIDEBAR_VALUE } from './sidebarValues';
export { SIDEBAR_VALUE };

export const SIDEBAR_ITEMS = [
  {
    label: 'LEGEND',
    value: SIDEBAR_VALUE.legend,
    icon: <DashboardIcon fontSize="medium" />
  },
  {
    label: 'LABELS',
    value: SIDEBAR_VALUE.labels,
    icon: <LabelIcon fontSize="medium" />
  },
  {
    label: 'TAGS',
    value: SIDEBAR_VALUE.tags,
    icon: <LocalOffer fontSize="medium" />
  },
  {
    label: 'EXTERNAL IDS',
    value: SIDEBAR_VALUE.externalFamilyIds,
    icon: <CollectionsBookmarkIcon fontSize="medium" />
  },
  {
    label: 'FAMILY NOTE',
    value: SIDEBAR_VALUE.familyNote,
    icon: <NoteIcon fontSize="medium" />
  },
  {
    label: 'CLINICAL RECORDS',
    value: SIDEBAR_VALUE.clinicalNotes,
    icon: <DescriptionIcon fontSize="medium" />
  },
  {
    label: 'PEDIGREE ANALYSIS',
    value: SIDEBAR_VALUE.pedigreeMatch,
    icon: <InsightsIcon fontSize="medium" />
  },
  {
    label: 'PATHWAYS',
    value: SIDEBAR_VALUE.pathways,
    icon: <AltRouteIcon fontSize="medium" />
  },
  // CLINICAL INSIGHTS (Profile Match / phenomizer) is intentionally NOT in
  // the family-level sidebar. Its output is specific to one individual's
  // phenotype/disease profile, so it only appears in getUserSidebarItems
  // (the per-individual sidebar) when a family member is selected.
  {
    label: 'FAMILY HISTORY',
    value: SIDEBAR_VALUE.familyHistory,
    icon: <AccountTreeIcon fontSize="medium" />
  },
  {
    label: 'EXPORT',
    value: SIDEBAR_VALUE.export,
    icon: <GetAppIcon fontSize="medium" />
  }
];

// eslint-disable-next-line no-unused-vars
export const getUserSidebarItems = ({ showPatientAccessLabel, showEmrTab }) =>
  [
    {
      label: 'LEGEND',
      value: SIDEBAR_VALUE.legend,
      icon: <DashboardIcon fontSize="medium" />
    },
    {
      label: 'LABELS',
      value: SIDEBAR_VALUE.labels,
      icon: <LabelIcon fontSize="medium" />
    },
    {
      label: 'TAGS',
      value: SIDEBAR_VALUE.tags,
      icon: <LocalOffer fontSize="medium" />
    },
    {
      label: 'ADD RELATIVE',
      value: SIDEBAR_VALUE.addRelative,
      icon: <PersonAddIcon fontSize="medium" />
    },
    // only show EMR if enabled
    ...(showEmrTab
      ? [
          {
            label: 'EMR',
            value: SIDEBAR_VALUE.emr,
            icon: <LaunchIcon fontSize="medium" />
          }
        ]
      : []),
    {
      label: 'PERSON',
      value: SIDEBAR_VALUE.person,
      icon: <PersonIcon fontSize="medium" />
    },
    // Show when user is proband & don't have email
    // showPatientAccessLabel && {
    //   label: 'SET UP PATIENT ACCESS',
    //   value: SIDEBAR_VALUE.setupPatientAccess,
    //   icon: <VerifiedUserIcon />
    // },
    {
      label: 'CLINICAL RECORDS',
      value: SIDEBAR_VALUE.clinicalNotes,
      icon: <DescriptionIcon fontSize="medium" />
    },
    {
      label: 'FORM SUBMISSIONS',
      value: SIDEBAR_VALUE.formSubmission,
      icon: <AssignmentIcon fontSize="medium" />
    },
    {
      label: 'PATHWAYS',
      value: SIDEBAR_VALUE.pathways,
      icon: <AltRouteIcon fontSize="medium" />
    },
    {
      label: 'DISEASES',
      value: SIDEBAR_VALUE.diagnoses,
      icon: <LocalHospitalIcon fontSize="medium" />
    },
    {
      label: 'RISK SCORES',
      value: SIDEBAR_VALUE.riskFactors,
      icon: <AssignmentLateIcon fontSize="medium" />
    },
    {
      label: 'PHENOTYPES',
      value: SIDEBAR_VALUE.phenotypes,
      icon: <BallotIcon fontSize="medium" />
    },
    {
      label: 'GENOTYPES',
      value: SIDEBAR_VALUE.genotypes,
      icon: <BlurLinearIcon fontSize="medium" />
    },
    {
      label: 'CLINICAL INSIGHTS',
      value: SIDEBAR_VALUE.suggestions,
      icon: <AssistantIcon fontSize="medium" />
    },
    {
      label: 'PEDIGREE ANALYSIS',
      value: SIDEBAR_VALUE.pedigreeMatch,
      icon: <InsightsIcon fontSize="medium" />
    },

    {
      label: 'FAMILY HISTORY',
      value: SIDEBAR_VALUE.familyHistory,
      icon: <AccountTreeIcon fontSize="medium" />
    },
    {
      label: 'RECORD STATUS',
      value: SIDEBAR_VALUE.recordStatus,
      icon: (
        <LinkIcon
          sx={{
            fontSize: 'medium'
          }}
        />
      )
    },
    {
      label: 'EXPORT',
      value: SIDEBAR_VALUE.export,
      icon: <GetAppIcon fontSize="medium" />
    }
  ].filter(Boolean);

export const STUDENT_SIDEBAR_ITEMS = [
  {
    label: 'LEGEND',
    value: SIDEBAR_VALUE.legend,
    icon: <DashboardIcon fontSize="medium" />
  },
  {
    label: 'LABELS',
    value: SIDEBAR_VALUE.labels,
    icon: <LabelIcon fontSize="medium" />
  },
  {
    label: 'EXPORT',
    value: SIDEBAR_VALUE.export,
    icon: <GetAppIcon fontSize="medium" />
  }
];

export const getStudentUserSidebarItems = (showPatientAccessLabel) =>
  [
    {
      label: 'LEGEND',
      value: SIDEBAR_VALUE.legend,
      icon: <DashboardIcon fontSize="medium" />
    },
    {
      label: 'ADD RELATIVE',
      value: SIDEBAR_VALUE.addRelative,
      icon: <PersonAddIcon fontSize="medium" />
    },
    {
      label: 'LABELS',
      value: SIDEBAR_VALUE.labels,
      icon: <LabelIcon fontSize="medium" />
    },
    {
      label: 'PERSON',
      value: SIDEBAR_VALUE.person,
      icon: <PersonIcon fontSize="medium" />
    },
    // Show when user is proband & don't have email
    showPatientAccessLabel && {
      label: 'SET UP PATIENT ACCESS',
      value: SIDEBAR_VALUE.setupPatientAccess,
      icon: <VerifiedUserIcon fontSize="medium" />
    },
    {
      label: 'EXPORT',
      value: SIDEBAR_VALUE.export,
      icon: <GetAppIcon fontSize="medium" />
    }
  ].filter(Boolean);

export const checkRelationValid = (selected_gender, deepCopy, formValues) => {
  const spouse = deepCopy.find((p) => p.pid === formValues.spouse);

  const child = deepCopy.some(
    (p) =>
      (p.mother === formValues.pid || p.father === formValues.pid) && !p.hidden
  );

  if (!spouse || !child) return { status: true };

  if (
    (selected_gender === GENDER.female &&
      [GENDER.female, GENDER.unknown].includes(spouse.gender)) ||
    (spouse.gender === GENDER.female &&
      [GENDER.female, GENDER.unknown].includes(selected_gender))
  ) {
    return {
      status: false,
      message:
        'This relationship has offspring and no valid sperm donor detected.  Please update gender. '
    };
  }

  const relationValid =
    (selected_gender === GENDER.female && spouse.gender === GENDER.male) ||
    (spouse.gender === GENDER.female && selected_gender === GENDER.male);

  if (relationValid) {
    return { status: true };
  } else {
    return {
      status: false,
      message:
        ' This relationship has offspring and no valid egg donor detected.  Please update gender. '
    };
  }
};

export const replacer = (key, value) => {
  if (typeof value === 'object' && value !== null) {
    if (key === 'parent_node') {
      return undefined;
    }
  }
  return value;
};

const extractFamilyId = (familyMemberId) => {
  if (!familyMemberId) return '';
  const parts = familyMemberId.split('-');
  parts.pop();
  return parts.join('-');
};

export const formatPersonSummary = (data) => {
  if (!data) return;
  const {
    familyMemberId,
    firstName,
    lastName,
    dob,
    hospitalNumber,
    healthcareId,
    proBandId
  } = data;

  const familyId = extractFamilyId(proBandId || familyMemberId);
  const upn = proBandId ? '1' : data.upn;

  const idPart = `${familyId}, UPN ${upn}`;
  const namePart = `${firstName || ''} ${lastName || ''}`.trim();
  const dobPart = dob ? `${dob}` : '';
  const hospitalPart = hospitalNumber
    ? `Hospital Number; ${hospitalNumber}`
    : '';
  const healthcarePart = healthcareId ? `Healthcare ID; ${healthcareId}` : '';

  return { idPart, namePart, dobPart, hospitalPart, healthcarePart };
};

export const deselectAllOutlines = () => {
  d3.selectAll('.te')
    .style('stroke-width', function () {
      return '.11em';
    })
    .style('stroke', 'black');
};

export const selectOutline = (index, theme) => {
  d3.selectAll('.te')
    .style('stroke-width', function (d, i) {
      return i === index ? '.25em' : '.11em';
    })
    .style('stroke', function (d, i) {
      return i === index ? theme.accent : 'black';
    });
};

export const getNodeFromIndex = (index) => {
  let res = {};
  d3.selectAll('.te').datum((d, i) => {
    if (i == index) {
      res = d?.data;
      if (!res) res = d;
    }
  });
  return res;
};

export const distance = (point1, point2) => {
  return Math.sqrt(
    Math.pow(point1.x - point2.x, 2) + Math.pow(point1.y - point2.y, 2)
  );
};
